<template>
  <div class="district-town-container">
    <!-- ページヘッダー -->
    <div class="page-header mb-4">
      <div class="header-content">
        <h4 class="mb-0">
          <i class="bi bi-map me-2 text-primary"></i>地区町名設定
        </h4>
        <BaseButton
          variant="secondary"
          size="sm"
          @click="$emit('back-to-menu')"
        >
          <i class="bi bi-arrow-left me-2"></i>メニューに戻る
        </BaseButton>
      </div>
    </div>

    <!-- データテーブル -->
    <InfoTableSection
      title=""
      :headers="['地区町名', '操作']"
      :items="districtTowns"
      :loading="loading"
      :error="error"
      empty-message="登録されている地区町名がありません"
      :pagination="{ enabled: true, perPage: 10 }"
      @action="(payload) => emit('table-action', payload)"
      @refresh="$emit('refresh')"
    >


      <template #tableRow="{ item }">
        <BTd class="district-town-name">
          <ContentText color="default">{{ item.districtTownName }}</ContentText>
        </BTd>
        <BTd class="text-center">
          <BaseButton
            size="sm"
            variant="success"
            class="me-2"
            @click="$emit('show-edit-modal', item)"
          >
            <i class="bi bi-pencil-square me-1"></i>編集
          </BaseButton>
          <BaseButton
            size="sm"
            variant="danger"
            @click="$emit('confirm-delete', item)"
          >
            <i class="bi bi-trash me-1"></i>削除
          </BaseButton>
        </BTd>
      </template>
    </InfoTableSection>

    <!-- 新規追加ボタン -->
    <div class="add-button-section mt-3">
      <BaseButton
        variant="primary"
        size="lg"
        @click="$emit('show-add-modal')"
      >
        <i class="bi bi-plus-circle me-2"></i>新規追加
      </BaseButton>
    </div>

    <!-- 追加・編集モーダル -->
    <BaseModal
      modal-id="district-town-form-modal"
      :model-value="showModal"
      @update:model-value="$emit('close-modal')"
      :title="isEditMode ? '地区町名の編集' : '新しい地区町名の追加'"
      size="md"
      @cancel="$emit('close-modal')"
    >
      <template #default>
        <DistrictTownForm
          :model-value="formData"
          :errors="formErrors"
          :is-edit="isEditMode"
          @update:model-value="$emit('update:form-data', $event)"
          @submit="emit('submit')"
        />
      </template>

      <template #footer>
        <div class="d-flex justify-content-end gap-2">
          <BaseButton
            variant="secondary"
            @click="$emit('close-modal')"
          >
            キャンセル
          </BaseButton>
          <BaseButton
            variant="primary"
            @click="emit('submit')"
          >
            <i class="bi bi-check me-1"></i>
            {{ isEditMode ? '更新' : '追加' }}
          </BaseButton>
        </div>
      </template>
    </BaseModal>

    <!-- 削除確認モーダル -->
    <BaseModal
      modal-id="delete-district-town-modal"
      :model-value="showDeleteModal"
      @update:model-value="$emit('close-delete-modal')"
      title="削除確認"
      variant="danger"
      confirm-text="削除"
      @confirm="$emit('delete-town', deleteTarget)"
      @cancel="$emit('close-delete-modal')"
    >
      <p class="mb-3">
        <strong>{{ deleteTarget?.districtTownName }}</strong>を削除してもよろしいですか？
      </p>
      <div class="alert alert-warning">
        <i class="bi bi-exclamation-triangle me-2"></i>
        この操作は元に戻すことができません。
      </div>
    </BaseModal>
  </div>
</template>

<script setup>
import { BTd } from 'bootstrap-vue-next'
import BaseButton from '@/components/atoms/BaseButton.vue'
import BaseModal from '@/components/molecules/BaseModal.vue'
import ContentText from '@/components/atoms/ContentText.vue'
import InfoTableSection from '@/components/organisms/InfoTableSection.vue'
import DistrictTownForm from './DistrictTownForm.vue'

const props = defineProps({
  districtTowns: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  },
  error: {
    type: String,
    default: null
  },
  showModal: {
    type: Boolean,
    default: false
  },
  showDeleteModal: {
    type: Boolean,
    default: false
  },
  isEditMode: {
    type: Boolean,
    default: false
  },
  formData: {
    type: Object,
    default: () => ({ districtTownName: '' })
  },
  formErrors: {
    type: Object,
    default: () => ({})
  },
  deleteTarget: {
    type: Object,
    default: null
  }
})

const emit = defineEmits([
  'show-add-modal',
  'show-edit-modal',
  'confirm-delete',
  'add-town',
  'edit-town',
  'delete-town',
  'close-modal',
  'close-delete-modal',
  'back-to-menu',
  'refresh',
  'table-action',
  'refresh',
  'table-action',
  'submit',
  'update:form-data'
])

</script>

<style lang="scss" scoped>
.district-town-container {
  padding: 1.5rem 0;
}

.page-header {
  .header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-left: 2rem;
    margin-right: 2rem;
    padding-bottom: 1rem;
    border-bottom: 2px solid #e9ecef;
  }

  h4 {
    color: #1f2937;
    font-weight: 600;
    flex: 1;
    display: flex;
    align-items: center;
    margin-bottom: 0;
  }
}

.district-town-name {
  font-weight: 600;
  color: #374151;
}

/* 新規追加ボタンエリア */
.add-button-section {
  margin-left: 2rem;
  margin-right: 2rem;
  margin-top: 1rem;
}

/* ボタンアニメーション */
.btn-outline-primary:hover,
.btn-outline-danger:hover {
  transform: translateY(-1px);
  transition: all 0.2s ease;
}

.btn-outline-primary:hover {
  box-shadow: 0 4px 8px rgba(0, 123, 255, 0.3);
}

.btn-outline-danger:hover {
  box-shadow: 0 4px 8px rgba(220, 53, 69, 0.3);
}

// 戻るボタンのスタイリング
.page-header button {
  border-radius: 0.5rem;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
}

/* モバイル対応 */
@media (max-width: 768px) {
  .page-header {
    .header-content {
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;
      margin-left: 1rem;
      margin-right: 1rem;
    }

    h4 {
      order: 1; // モバイルではタイトルを上に
      font-size: 1.25rem;
    }

    button {
      order: 2; // ボタンを下に
      align-self: flex-end; // 右寄せ維持
    }
  }

  .add-button-section {
    margin-left: 1rem;
    margin-right: 1rem;
  }
}
</style>
